import { useAuth } from '@/contexts/auth-context';
import { UserRole, ModuleKey, ModuleRoleValue } from '@/lib/types';

const ROLE_LEVEL: Record<string, number> = {
  Admin: 5,
  Manager: 4,
  Inspector: 3,
  Supervisor: 3,
  User: 2,
  Viewer: 1,
};

export type BoardPersona =
  | 'owner'
  | 'supervisor'
  | 'payroll'
  | 'pm'
  | 'gear'
  | 'compliance'
  | 'crew'
  | 'crew_tech'
  | 'viewer';

export const MODULE_KEYS: readonly ModuleKey[] = [
  'inventory',
  'safety',
  'crew',
  'projects',
  'payroll',
  'compliance',
  'work',
];

/**
 * Enabled modules the user can actually open: the org has the module on AND
 * the user's effective role for it is not `no_access`.
 */
export function getAccessibleModules(
  enabledModules: readonly string[],
  getModuleRole: (module: ModuleKey) => ModuleRoleValue,
): ModuleKey[] {
  return MODULE_KEYS.filter(
    (module) => enabledModules.includes(module) && getModuleRole(module) !== 'no_access',
  );
}

/**
 * Post-login landing. A user whose only accessible module is H3Pay lands on
 * the Pay home; everyone else keeps the persona rule (techs → My Dashboard,
 * all other personas → H3ightboard). `accessibleModules` is optional so
 * callers without module data keep the existing behaviour.
 */
export function getBoardLandingRoute(
  boardPersona: BoardPersona,
  accessibleModules?: readonly ModuleKey[],
) {
  if (accessibleModules?.length === 1 && accessibleModules[0] === 'payroll') {
    return '/payroll';
  }
  return boardPersona === 'crew_tech' ? '/my-dashboard' : '/h3ightboard';
}

/**
 * Org roles that may open the org-wide Admin Dashboard (`/dashboard`) and see
 * the "Admin" entry in navigation. Mirrors the backend's
 * `IsOrgAdminOrManagerGroup` on `/api/v1/dashboard/`.
 */
export const ADMIN_DASHBOARD_ROLES: readonly UserRole[] = ['Admin', 'Manager'];

export function canViewAdminDashboard(role: UserRole | null | undefined): boolean {
  return Boolean(role) && ADMIN_DASHBOARD_ROLES.includes(role as UserRole);
}

export interface Permissions {
  canManageUsers: boolean;
  canManageOrganization: boolean;
  canManageInvitations: boolean;
  canManageTemplates: boolean;
  canViewAuditLogs: boolean;
  canCreateAssets: boolean;
  canEditAssets: boolean;
  canDeleteAssets: boolean;
  canCreateInspections: boolean;
  canCreateDeployments: boolean;
  canApproveDeployments: boolean;
  canCreateRopeCuts: boolean;
  canCreateMaintenance: boolean;
  canExportData: boolean;
  canFulfillGearRequests: boolean;
  /** Clients and client contacts are commercial records: org Admins and
   *  Managers, or managers of H3Books/H3Project, only (server-enforced). */
  canViewClients: boolean;
  /** May add people (Add Person, Import CSV). Mirrors the server's
   *  `core:permissions:org_admin_or_manager` role template: org Admin or
   *  Manager. */
  canCreatePeople: boolean;
  /** May add technician profiles (Add Technician, Make Technician). Server
   *  truth from /api/v1/profile/ (`can_manage_onboarding` is the same
   *  `crew:viewsets:admin_or_manager` capability that tech create checks),
   *  so per-person switch-offs are already applied. */
  canCreateTechs: boolean;
  isAdmin: boolean;
  isManager: boolean;
  isInspector: boolean;
  isSupervisor: boolean;
  isUser: boolean;
  isViewer: boolean;
  isPlatformStaff: boolean;
  /** True when the user may open the org-wide Admin Dashboard (Admin/Manager) */
  canViewAdminDashboard: boolean;
  /** True when the user's role is at or above the given minimum */
  hasMinRole: (minRole: string) => boolean;
  /** Resolved role for a specific module */
  getModuleRole: (module: ModuleKey) => ModuleRoleValue;
  /** True when the user's effective role for the module is anything but `no_access`. */
  hasModuleAccess: (module: ModuleKey | string) => boolean;
  /** True when the user's module role is at or above the given minimum */
  hasMinModuleRole: (module: ModuleKey, minRole: ModuleRoleValue) => boolean;
  /** Board edit capability from backend /api/v1/profile/ */
  canEditBoard: boolean;
  /** Board dispatch capability from backend /api/v1/profile/ */
  canDispatchBoard: boolean;
  /** Onboarding checklist management capability from backend /api/v1/profile/ */
  canManageOnboarding: boolean;
  /** Onboarding item completion capability from backend /api/v1/profile/ */
  canCompleteOnboarding: boolean;
  /** Work module admin-or-manager capability from backend /api/v1/profile/ */
  canAdminWork: boolean;
  /** JSA re-validation resolution capability from backend /api/v1/profile/ */
  canResolveJSARevalidation: boolean;
  /** Gear-store write access (inventory admin_or_manager): add/edit/retire
   *  assets, check gear in/out, send gear, log maintenance. */
  canManageGear: boolean;
  /** May sign off inspections (inspection:create: admin, inspector, or
   *  Inspection Certified) from backend /api/v1/profile/. */
  canRecordInspections: boolean;
  /** May create/edit jobs and manage a job's team (staffing board, job Team
   *  tab). Server-computed on /api/v1/profile/ (can_manage_jobs) by the same
   *  FoundationJobWritePermission the write endpoints use, so module
   *  enablement and per-person switch-offs are already applied. */
  canManageJobs: boolean;
  /** Deterministic board persona derived from backend capability flags then module roles */
  boardPersona: BoardPersona;
  /** True when the user may see monetary fields (daily_rate, daily_crew_cost).
   *  Mirrors the backend _can_see_financials predicate from S04:
   *  Manager+ org role OR explicit payroll ModuleRole. */
  canViewFinancials: boolean;
  /** H3Pay Manager view: payroll module role >= manager (org Admins resolve
   *  to module admin). Gates the all-worker Timesheets, Per Diem, Expenses
   *  and Mileage views and their Approve/Reject controls. Org Managers
   *  without a payroll module role do not qualify (server-enforced). */
  canManagePayroll: boolean;
}

const MODULE_ROLE_LEVEL: Record<string, number> = {
  admin: 5,
  manager: 4,
  inspector: 3,
  supervisor: 3,
  user: 2,
  viewer: 1,
  no_access: 0,
};

function orgRoleToModuleFallback(orgRole: UserRole | null | undefined): ModuleRoleValue {
  return orgRole === 'Admin' ? 'admin' : 'no_access';
}

/**
 * Deterministic board-persona resolution.
 *
 * Precedence (first match wins):
 *   1. Backend board edit capability     → owner      (HasCapability result)
 *   2. Backend board dispatch capability → supervisor (HasCapability result)
 *   3. Org Admin/Manager                 → owner      (unambiguous org-level)
 *   4. Org Supervisor/Inspector          → supervisor (unambiguous org-level)
 *   5. payroll  module ≥ manager         → payroll   (module-role tiebreak)
 *   6. projects module ≥ manager           → pm
 *   7. inventory module ≥ manager          → gear
 *   8. safety   module ≥ manager           → compliance
 *   9. crew     module ≥ manager          → crew
 *  10. Org User (plain)                  → crew_tech  (read-only personal view)
 *  11. Fallback                          → viewer
 *
 * Note: Manager maps to owner (rollup) at the org level because
 * orgRoleToModuleFallback returns 'manager' for all modules, making
 * every module-role tiebreak match. Treating Manager as org-level
 * avoids an arbitrary first-module winner and gives Managers the
 * same rollup view Admins get.
 */
export function deriveBoardPersona(
  getModuleRole: (m: ModuleKey) => ModuleRoleValue,
  orgRole: UserRole | null | undefined,
  canEditBoard = false,
  canDispatchBoard = false,
): BoardPersona {
  // 1. Backend capability flags are the source of truth for board access
  if (canEditBoard) return 'owner';
  if (canDispatchBoard) return 'supervisor';

  // 3. Org-level first where unambiguous
  if (orgRole === 'Admin' || orgRole === 'Manager') return 'owner';
  if (orgRole === 'Supervisor' || orgRole === 'Inspector') return 'supervisor';

  // 4. Module-role tiebreak (deterministic order)
  const managerThreshold = MODULE_ROLE_LEVEL['manager'];
  if ((MODULE_ROLE_LEVEL[getModuleRole('payroll')] ?? 0) >= managerThreshold) return 'payroll';
  if ((MODULE_ROLE_LEVEL[getModuleRole('projects')] ?? 0) >= managerThreshold) return 'pm';
  if ((MODULE_ROLE_LEVEL[getModuleRole('inventory')] ?? 0) >= managerThreshold) return 'gear';
  if ((MODULE_ROLE_LEVEL[getModuleRole('safety')] ?? 0) >= managerThreshold) return 'compliance';
  if ((MODULE_ROLE_LEVEL[getModuleRole('crew')] ?? 0) >= managerThreshold) return 'crew';

  // 5. Plain user → crew_tech (read-only personal focus)
  if (orgRole === 'User') return 'crew_tech';

  // 6. Viewer / unknown
  return 'viewer';
}

export function usePermissions(): Permissions {
  const { user } = useAuth();
  const role = user?.role;
  const moduleRoles = user?.module_roles ?? {};

  const isAdmin = role === 'Admin';
  const isManager = role === 'Manager';
  const isInspector = role === 'Inspector';
  const isSupervisor = role === 'Supervisor';
  const isUser = role === 'User';
  const isViewer = role === 'Viewer';
  const isPlatformStaff = user?.is_staff ?? false;

  const hasMinRole = (minRole: string): boolean => {
    if (!role) return false;
    return (ROLE_LEVEL[role] ?? 0) >= (ROLE_LEVEL[minRole] ?? 0);
  };

  const getModuleRole = (module: ModuleKey): ModuleRoleValue => {
    if (isAdmin) return 'admin';
    return moduleRoles[module] ?? orgRoleToModuleFallback(role);
  };

  const hasMinModuleRole = (module: ModuleKey, minRole: ModuleRoleValue): boolean => {
    const actual = getModuleRole(module);
    return (MODULE_ROLE_LEVEL[actual] ?? 0) >= (MODULE_ROLE_LEVEL[minRole] ?? 0);
  };

  const hasModuleAccess = (module: ModuleKey | string): boolean =>
    getModuleRole(module as ModuleKey) !== 'no_access';

  const canManageJobs = user?.can_manage_jobs ?? user?.can_edit_board ?? false;
  const canEditBoard = user?.can_edit_board ?? canManageJobs;
  const canDispatchBoard = user?.can_dispatch_board ?? false;
  const canManageOnboarding = user?.can_manage_onboarding ?? false;
  const canCompleteOnboarding = user?.can_complete_onboarding ?? false;
  const canAdminWork = user?.can_admin_work ?? false;
  const canResolveJSARevalidation = user?.can_resolve_jsa_revalidation ?? false;
  const canManageGear =
    isAdmin || isManager || hasMinModuleRole('inventory', 'manager');
  const canRecordInspections = user?.can_record_inspections ?? false;

  const boardPersona = deriveBoardPersona(getModuleRole, role, canEditBoard, canDispatchBoard);
  const canViewFinancials = isAdmin || isManager || moduleRoles.payroll != null;
  const isScopedManager = (module: ModuleKey) =>
    (MODULE_ROLE_LEVEL[moduleRoles[module] ?? ''] ?? 0) >= MODULE_ROLE_LEVEL.manager;
  const canViewClients =
    isAdmin || isManager || isScopedManager('projects') || isScopedManager('work');
  const canManagePayroll = hasMinModuleRole('payroll', 'manager');

  return {
    canManageUsers: isAdmin,
    canManageOrganization: isAdmin,
    canManageInvitations: isAdmin,
    canManageTemplates: isAdmin || isManager,
    canViewAuditLogs: isAdmin,
    canCreateAssets: canManageGear,
    canEditAssets: canManageGear,
    canDeleteAssets: isAdmin,
    canCreateInspections: canRecordInspections,
    canCreateDeployments: canManageGear,
    canApproveDeployments: canManageGear,
    canCreateRopeCuts: canManageGear,
    canCreateMaintenance: hasMinModuleRole('inventory', 'inspector'),
    canExportData: hasMinRole('User'),
    canFulfillGearRequests: isAdmin || isManager || hasMinModuleRole('inventory', 'manager'),
    canViewClients,
    canCreatePeople: isAdmin || isManager,
    canCreateTechs: canManageOnboarding,
    isAdmin,
    isManager,
    isInspector,
    isSupervisor,
    isUser,
    isViewer,
    isPlatformStaff,
    canViewAdminDashboard: canViewAdminDashboard(role),
    hasMinRole,
    getModuleRole,
    hasModuleAccess,
    hasMinModuleRole,
    canEditBoard,
    canDispatchBoard,
    canManageOnboarding,
    canCompleteOnboarding,
    canAdminWork,
    canResolveJSARevalidation,
    canManageGear,
    canRecordInspections,
    canManageJobs,
    boardPersona,
    canViewFinancials,
    canManagePayroll,
  };
}

export function useRequireRole(allowedRoles: UserRole[]): boolean {
  const { user } = useAuth();
  if (!user?.role) return false;
  return allowedRoles.includes(user.role);
}
